import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  MessageSquare,
  Phone,
  Save,
  CheckCircle2,
  Sliders,
  Sparkles,
  Eye,
  GripVertical,
} from 'lucide-react';
import { buildWaUrl, formatPhoneTel } from '../../lib/contactUtils.ts';

export default function FloatingWidgetManager() {
  const { settings, updateSettingsOnServer, showToast } = useApp();

  const [widgetConfig, setWidgetConfig] = useState(
    settings?.contactWidget || {
      enabled: true,
      whatsappNumber: settings?.contact?.whatsappNumber || '6281234567890',
      phoneNumber: settings?.contact?.phone || '+62 812-3456-7890',
      whatsappLabel: 'WhatsApp Advisory',
      phoneLabel: 'Call Office',
      statusBadgeText: '🟢 Online 24/7 | Fast Response',
      defaultWaMessage:
        'Hello Greenjobs ITB Team, I would like to inquire about sustainability services and partnerships.',
      popupGreeting: 'Need quick sustainability advisory or proposal? Contact us directly!',
      showPhone: true,
      showWhatsapp: true,
    }
  );

  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<string | null>(null);

  const primaryColor = settings?.theme?.primaryColor || '#1B4332';

  const handleSave = async () => {
    if (!settings) return;
    setIsSaving(true);
    try {
      const activeWa = widgetConfig.whatsappNumber?.trim() || '';
      const activePhone = widgetConfig.phoneNumber?.trim() || '';

      const updatedWidget = {
        ...widgetConfig,
        whatsappNumber: activeWa,
        phoneNumber: activePhone,
      };

      const updatedContact = {
        ...settings.contact,
        whatsappNumber: activeWa || settings.contact?.whatsappNumber || '',
        phone: activePhone || settings.contact?.phone || '',
      };

      await updateSettingsOnServer({
        ...settings,
        contactWidget: updatedWidget,
        contact: updatedContact,
      });

      const timeStr = new Date().toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      setLastSavedTime(timeStr);
      showToast('Pengaturan Tombol Kontak Melayang berhasil disimpan ke database!', 'success');
    } catch (err: any) {
      showToast(err.message || 'Gagal menyimpan pengaturan widget melayang', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const previewWaUrl = buildWaUrl(
    widgetConfig.whatsappNumber || '6281234567890',
    widgetConfig.defaultWaMessage || ''
  );
  const previewPhoneTel = formatPhoneTel(widgetConfig.phoneNumber || '+62 812-3456-7890');

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* Header & Save Action */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-bold mb-2 border border-emerald-200">
            <Sliders className="w-3.5 h-3.5 text-emerald-600" />
            <span>Bagian dari Tampilan Front-End Beranda</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
            Tombol Kontak WhatsApp & Telepon Melayang
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Atur tombol mengambang di pojok layar website agar pengunjung mudah menghubungi hotline kantor atau chat WhatsApp CS.
          </p>
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2.5 rounded-xl text-white font-bold text-xs sm:text-sm shadow-md flex items-center gap-2 transition-all cursor-pointer hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: primaryColor }}
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Menyimpan...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Simpan Pengaturan Widget</span>
              </>
            )}
          </button>

          {lastSavedTime && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-50 border border-emerald-300 rounded-lg text-emerald-800 text-[11px] font-bold">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              <span>Tersimpan ({lastSavedTime})</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Settings Card */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-6">
        {/* Toggle On/Off Header */}
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold">
              <MessageSquare className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">
                Status Widget Melayang di Halaman Publik
              </h3>
              <p className="text-xs text-slate-500">
                Aktifkan atau sembunyikan widget melayang untuk seluruh halaman website.
              </p>
            </div>
          </div>

          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={widgetConfig.enabled !== false}
              onChange={(e) => setWidgetConfig({ ...widgetConfig, enabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
          </label>
        </div>

        {/* Input Form Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Nomor WhatsApp CS (Format internasional, cth: 6281234567890)
            </label>
            <input
              type="text"
              value={widgetConfig.whatsappNumber || ''}
              onChange={(e) => setWidgetConfig({ ...widgetConfig, whatsappNumber: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="6281234567890"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Nomor Telepon Hotline / Kantor
            </label>
            <input
              type="text"
              value={widgetConfig.phoneNumber || ''}
              onChange={(e) => setWidgetConfig({ ...widgetConfig, phoneNumber: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="+62 22 2500935"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Label Tombol WhatsApp
            </label>
            <input
              type="text"
              value={widgetConfig.whatsappLabel || ''}
              onChange={(e) => setWidgetConfig({ ...widgetConfig, whatsappLabel: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="WhatsApp Advisory"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Label Tombol Telepon
            </label>
            <input
              type="text"
              value={widgetConfig.phoneLabel || ''}
              onChange={(e) => setWidgetConfig({ ...widgetConfig, phoneLabel: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Call Office"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Pesan Popup Sapaan (Greeting Tooltip)
            </label>
            <input
              type="text"
              value={widgetConfig.popupGreeting || ''}
              onChange={(e) => setWidgetConfig({ ...widgetConfig, popupGreeting: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Need quick sustainability advisory or proposal? Contact us directly!"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Teks Status Bubble Online
            </label>
            <input
              type="text"
              value={widgetConfig.statusBadgeText || ''}
              onChange={(e) => setWidgetConfig({ ...widgetConfig, statusBadgeText: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="🟢 Online 24/7 | Fast Response"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Pesan Awal Otomatis Chat WhatsApp (Pre-filled Message)
            </label>
            <textarea
              rows={2}
              value={widgetConfig.defaultWaMessage || ''}
              onChange={(e) => setWidgetConfig({ ...widgetConfig, defaultWaMessage: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Hello Greenjobs ITB Team, I would like to inquire about sustainability services and partnerships."
            />
          </div>
        </div>

        {/* Visibility Toggles */}
        <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={widgetConfig.showWhatsapp !== false}
              onChange={(e) => setWidgetConfig({ ...widgetConfig, showWhatsapp: e.target.checked })}
              className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-xs font-bold text-slate-700">Tampilkan Tombol WhatsApp</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={widgetConfig.showPhone !== false}
              onChange={(e) => setWidgetConfig({ ...widgetConfig, showPhone: e.target.checked })}
              className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-xs font-bold text-slate-700">Tampilkan Tombol Telepon</span>
          </label>
        </div>

        {/* Live Interactive Preview Box */}
        <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-bold text-slate-800">Pratinjau Widget di Pojok Layar</span>
            </div>
            <span className="text-[10px] text-slate-400 font-medium">Bisa digeser (draggable) oleh pengguna</span>
          </div>

          <div className="p-6 bg-slate-900 rounded-xl border border-slate-800 flex flex-col items-end gap-2.5">
            {/* Tooltip greeting preview */}
            <div className="bg-slate-950/95 text-white px-3 py-2 rounded-xl shadow-md border border-emerald-500/30 flex items-center gap-2 max-w-sm">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-ping shrink-0" />
              <p className="text-[11px] text-slate-200 leading-tight">
                {widgetConfig.popupGreeting || 'Need quick sustainability advisory or proposal? Contact us directly!'}
              </p>
            </div>

            {/* Main button stack preview */}
            <div className="flex items-center gap-2 bg-white/95 p-1.5 rounded-full shadow-lg border-2 border-emerald-500/40">
              <div className="px-2 text-slate-400 flex items-center gap-1 cursor-grab">
                <GripVertical className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-[9px] font-bold uppercase text-slate-500">Drag</span>
              </div>

              {widgetConfig.showWhatsapp !== false && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-extrabold text-xs shadow-xs">
                  <MessageSquare className="w-3.5 h-3.5" />
                  <span>{widgetConfig.whatsappLabel || 'WhatsApp Advisory'}</span>
                </div>
              )}

              {widgetConfig.showPhone !== false && (
                <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-900 text-white font-bold text-xs shadow-xs">
                  <Phone className="w-3 h-3 text-emerald-400" />
                  <span>{widgetConfig.phoneLabel || 'Call Office'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
